import { trackEvent } from '@/core/analytics'
import { withPlanFraming } from '@/core/analytics/plan-framing'
import { planTypeFromCode } from '@/core/analytics/plan-type'

// Active plan's type, attached to every journey event so analyses can split
// journey metrics per plan. Canonical values: vgn | lmp | taiChi | tns | kds | hpm.
let planType: string | null = null

export function setJourneyAnalyticsPlanType(
  planCode: string | null | undefined,
) {
  planType = planCode ? planTypeFromCode(planCode) : null
}

function track(eventName: string, properties: Record<string, unknown>) {
  trackEvent(
    eventName,
    withPlanFraming(planType ? { planType, ...properties } : properties),
  )
}

// --- Journey map ---

const MAP_SCREEN = {
  screenName: 'journeyMap',
  screenType: 'page',
}

export function trackMapScreenView() {
  track('screenView', MAP_SCREEN)
}

export function trackDayTapped(
  dayState: 'active' | 'completed' | 'timer' | 'lockedFuture',
  dayId: string,
  dayNumber: number,
  weekNumber: number,
  goalNumber: number,
) {
  track('journeyDayTapped', {
    ...MAP_SCREEN,
    dayState,
    dayId,
    dayNumber,
    weekNumber,
    goalNumber,
  })
}

export function trackScrollToTodayTapped(
  direction: 'above' | 'below',
  goalNumber: number,
) {
  track('journeyScrollToTodayTapped', {
    ...MAP_SCREEN,
    direction,
    goalNumber,
  })
}

export function trackLoadFailed(errorType: string) {
  track('journeyLoadFailed', { ...MAP_SCREEN, errorType })
}

export function trackLoadRetryTapped() {
  track('journeyLoadRetryTapped', MAP_SCREEN)
}

export function trackDayCompletionRefreshFailed(
  dayId: string,
  errorType: string,
) {
  track('journeyDayCompletionRefreshFailed', {
    ...MAP_SCREEN,
    dayId,
    errorType,
  })
}

export function trackGoalCardTapped(
  goalNumber: number,
  isCompleted: boolean,
  isLocked: boolean,
) {
  track('journeyGoalCardTapped', {
    ...MAP_SCREEN,
    goalNumber,
    isCompleted,
    isLocked,
  })
}

export function trackWeekHeaderTapped(weekNumber: number, goalNumber: number) {
  track('journeyWeekHeaderTapped', {
    ...MAP_SCREEN,
    weekNumber,
    goalNumber,
  })
}

export function trackMilestoneBadgeTapped(
  weekNumber: number,
  goalNumber: number,
  isUnlocked: boolean,
) {
  track('journeyMilestoneBadgeTapped', {
    ...MAP_SCREEN,
    weekNumber,
    goalNumber,
    isUnlocked,
  })
}

export function trackPaginationTriggered(
  direction: 'prev' | 'next',
  fromGoalNumber: number,
) {
  track('journeyPaginationTriggered', {
    ...MAP_SCREEN,
    direction,
    fromGoalNumber,
  })
}

export function trackPaginationFailed(
  direction: 'prev' | 'next',
  errorType: string,
) {
  track('journeyPaginationFailed', {
    ...MAP_SCREEN,
    direction,
    errorType,
  })
}

// --- Profile ---

export function trackJourneyProfileSectionTapped(
  goalNumber: number,
  weeksCompleted: number,
  daysCompletedInWeek: number,
) {
  track('journeyProfileSectionTapped', {
    screenName: 'profile',
    screenType: 'page',
    goalNumber,
    weeksCompleted,
    daysCompletedInWeek,
  })
}

// --- Secondary screen views ---

export function trackGoalIntroScreenView() {
  track('screenView', {
    screenName: 'journeyGoalIntro',
    screenType: 'page',
  })
}

export function trackWeekIntroScreenView() {
  track('screenView', {
    screenName: 'journeyWeekIntro',
    screenType: 'sheet',
  })
}

export function trackTimerDayScreenView() {
  track('screenView', {
    screenName: 'journeyTimerDay',
    screenType: 'page',
  })
}

export function trackFutureDayScreenView() {
  track('screenView', {
    screenName: 'journeyFutureDay',
    screenType: 'page',
  })
}

export function trackDayPracticeScreenView() {
  track('screenView', {
    screenName: 'journeyDayPractice',
    screenType: 'sheet',
  })
}

// --- Milestone celebration ---

export function trackMilestoneCelebrationScreenView(milestone?: {
  goalNumber: number
  weekNumber: number
}) {
  track('screenView', {
    screenName: 'journeyMilestoneCelebration',
    screenType: 'modal',
    ...milestone,
  })
}

export function trackMilestoneCelebrationClosed(
  weekNumber: number,
  goalNumber: number,
) {
  track('journeyMilestoneCelebrationClosed', {
    screenName: 'journeyMilestoneCelebration',
    screenType: 'modal',
    weekNumber,
    goalNumber,
  })
}

// --- Day completion ---

export function trackDayCompletionScreenView() {
  track('screenView', {
    screenName: 'journeyDayCompletion',
    screenType: 'modal',
  })
}

export function trackDayCompletionClosed(
  dayId: string,
  dayNumber: number,
  weekNumber: number,
  goalNumber: number,
) {
  track('journeyDayCompletionClosed', {
    screenName: 'journeyDayCompletion',
    screenType: 'modal',
    dayId,
    dayNumber,
    weekNumber,
    goalNumber,
  })
}

// --- Goal completion ---

export function trackGoalCompletionScreenView() {
  track('screenView', {
    screenName: 'journeyGoalCompletion',
    screenType: 'modal',
  })
}

export function trackGoalCompletionClosed(goalNumber: number) {
  track('journeyGoalCompletionClosed', {
    screenName: 'journeyGoalCompletion',
    screenType: 'modal',
    goalNumber,
  })
}
